/**
 * Main configuration API for Axogen.
 * This module provides the primary `defineConfig` function and re-exports all configuration types.
 */

import {type AnyTarget, json} from "./targets.ts";
import * as z from "zod";
import {axogenConfigSchema, type ZodAxogenConfig} from "./zod_config.ts";
import {deepMerge, zodIssuesToErrors} from "../../utils/helpers.ts";
import type {AxogenConfig} from "./config.ts";
import {cmd} from "./commands.ts";
import {logger} from "../../utils/console/logger.ts";

/**
 * Define and validate an Axogen configuration.
 * This is the primary function users call to create their configuration.
 * Provides full type safety and validation using Zod schemas.
 * @param config - The Axogen configuration object with targets and commands
 * @returns Validated configuration object
 * @throws Error if configuration validation fails
 */
export function defineConfig<TTargets extends Record<string, AnyTarget>>(
    config: AxogenConfig<TTargets>
): ZodAxogenConfig {
    try {
        return axogenConfigSchema.parse({
            ...config,
            type: "AxogenConfig",
        });
    } catch (error) {
        if (error instanceof z.ZodError) {
            const validationErrors = zodIssuesToErrors(error.issues);

            logger.validation(
                `Configuration validation failed`,
                validationErrors
            );

            logger.logF(
                `<primary>💡</primary> <d>Check your config file structure.</d>`
            );

            throw new Error("Configuration validation failed");
        }

        throw new Error(
            `Failed to load config: ${
                error instanceof Error ? error.message : String(error)
            }`
        );
    }
}

/**
 * Extend an existing Axogen configuration with additional targets or commands.
 * NOTE: This function merges the source configuration with the extension,
 * in case of conflicts, the extension will ***OVERRIDE*** the source.
 * @param source - The source Axogen configuration to extend
 * @param extension - The Axogen configuration extension to merge with the source
 */
export function extendConfig<TTargets extends Record<string, AnyTarget>>(
    source: ZodAxogenConfig,
    extension: AxogenConfig<TTargets>
) {
    let validatedSource: ZodAxogenConfig;
    try {
        validatedSource = axogenConfigSchema.parse(source);
    } catch (error) {
        if (error instanceof z.ZodError) {
            const validationErrors = zodIssuesToErrors(error.issues);

            logger.validation(
                `Configuration validation of source failed`,
                validationErrors
            );

            logger.logF(
                `<primary>💡</primary> <d>Check your config file structure.</d>`
            );

            throw new Error("Configuration validation of source failed");
        }

        throw new Error(
            `Failed to load source config: ${
                error instanceof Error ? error.message : String(error)
            }`
        );
    }

    const validatedExtension = defineConfig(extension);
    const mergedConfig = deepMerge(validatedSource, validatedExtension);

    try {
        // Check one last time if the merged config is valid
        return axogenConfigSchema.parse(mergedConfig);
    } catch (error) {
        if (error instanceof z.ZodError) {
            const validationErrors = zodIssuesToErrors(error.issues);

            logger.validation(
                `Merged configuration validation failed`,
                validationErrors
            );

            logger.logF(
                `<primary>💡</primary> <d>Check your config file structure.</d>`
            );

            throw new Error("Merged configuration validation failed");
        }

        throw new Error(
            `Failed to validate merged config: ${
                error instanceof Error ? error.message : String(error)
            }`
        );
    }
}

// re-export the types for convenience
export type * from "./config.ts";
export * from "./config.ts";
export type * from "./zod_config.ts";
export * from "./zod_config.ts";
export type * from "./targets.ts";
export * from "./targets.ts";
export type * from "./zod_targets.ts";
export * from "./zod_targets.ts";
export type * from "./commands.ts";
export * from "./commands.ts";
export type * from "./zod_commands.ts";
export * from "./zod_commands.ts";

// Testing defineConfig (build will fail if tsc errors this)

const conf = defineConfig({
    targets: {
        someService: json({
            path: "output/someService.json",
            schema: z.object({
                someVar: z.string().describe("A variable for the service"),
            }),
            variables: {
                someVar: "value",
            },
        }),
    },
    commands: {
        someCommand: cmd({
            command: "echo 'Hello, World!'",
            help: "A simple command that echoes 'Hello, World!'",
        }),
        simple: "test",
        simpleFunction: (context) => {
            context.config.targets!.someService.variables.someVar;
        },
        complex: cmd({
            help: "A complex command with options and arguments",
            args: {
                someArg: z.number().describe("An argument for the command"),
            },
            exec: (context) => {
                const arg = context.args.someArg;
                const someVar =
                    context.config.targets!.someService.variables.someVar;
            },
        }),
    },
});
